import * as React from 'react'
import { Pencil, SlidersHorizontal, Undo2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ExpandingIconButton } from '@/components/ui/expanding-icon-button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { MembershipLevel } from '@/pages/MembershipLevelsPage.types'
import type { LevelRenewalSettingsDraft } from '@/pages/renewal-settings-support'

export function LevelRenewalOverrides({
  levels,
  drafts,
  errors,
  associationGraceDays,
  associationTouches,
  onDraftChange,
}: Props) {
  const [editing, setEditing] = React.useState<MembershipLevel | null>(null)
  const [lastEdited, setLastEdited] = React.useState<MembershipLevel | null>(null)
  const [shownErrorKey, setShownErrorKey] = React.useState('')
  const [graceDays, setGraceDays] = React.useState('')
  const [reminderTouches, setReminderTouches] = React.useState<ReminderTouch[]>([])
  const openEditor = React.useCallback(
    (level: MembershipLevel) => {
      setLastEdited(level)
      setGraceDays(
        (
          drafts[level.id]?.renewal_grace_days_override ??
          level.renewal_settings?.effective_grace_days ??
          0
        ).toString(),
      )
      setReminderTouches(
        drafts[level.id]?.renewal_reminder_touches_override ??
          level.renewal_settings?.reminder_touches_override ??
          level.renewal_settings?.effective_reminder_touches ??
          [],
      )
      setEditing(level)
    },
    [drafts],
  )

  React.useEffect(() => {
    const errorKey = errors.join('\n')
    if (!errorKey && shownErrorKey) {
      setShownErrorKey('')
      return
    }
    if (errorKey && lastEdited && errorKey !== shownErrorKey) {
      setShownErrorKey(errorKey)
      openEditor(lastEdited)
    }
  }, [errors, lastEdited, openEditor, shownErrorKey])

  const saveLevel = () => {
    if (!editing || !Number.isInteger(Number(graceDays))) return
    onDraftChange(editing, {
      renewal_grace_days_override: Number(graceDays),
      renewal_reminder_touches_override: reminderTouches,
    })
    setEditing(null)
  }

  const useDefaults = (level: MembershipLevel) => {
    onDraftChange(level, {
      renewal_grace_days_override: null,
      renewal_reminder_touches_override: null,
    })
  }
  return (
    <section className="px-5 py-4" aria-labelledby="level-overrides-heading">
      <h2
        className="mb-2 text-caption font-medium uppercase tracking-wide text-muted-foreground"
        id="level-overrides-heading"
      >
        Level overrides
      </h2>
      <div className="divide-y divide-border">
        {levels.map((level) => (
          <LevelRow
            key={level.id}
            level={level}
            draft={drafts[level.id]}
            onEdit={openEditor}
            onUseDefaults={useDefaults}
            associationGraceDays={associationGraceDays}
            associationTouches={associationTouches}
          />
        ))}
      </div>
      <LevelRenewalEditorDialog
        level={editing}
        graceDays={graceDays}
        reminderTouches={reminderTouches}
        errors={errors}
        onGraceDaysChange={setGraceDays}
        onClose={() => setEditing(null)}
        onSave={saveLevel}
      />
    </section>
  )
}

function LevelRenewalEditorDialog({
  level,
  graceDays,
  reminderTouches,
  errors,
  onGraceDaysChange,
  onClose,
  onSave,
}: {
  level: MembershipLevel | null
  graceDays: string
  reminderTouches: ReminderTouch[]
  errors: string[]
  onGraceDaysChange: (value: string) => void
  onClose: () => void
  onSave: () => void
}) {
  return (
    <Dialog open={level !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Customize {level?.name}
            {level?.renewal_term ? ` (${level.renewal_term.toLowerCase()})` : ''}
          </DialogTitle>
          <DialogDescription>Starts from the association settings.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Label htmlFor="level-grace-period" className="w-36 shrink-0">
            Grace period
          </Label>
          <Input
            id="level-grace-period"
            className="h-8 w-16"
            inputMode="numeric"
            value={graceDays}
            onChange={(event) => onGraceDaysChange(event.target.value)}
          />
          <span className="text-body-sm text-muted-foreground">days past the due date</span>
        </div>
        {errors.map((message) => (
          <p className="text-body-sm text-destructive" role="alert" key={message}>
            {message}
          </p>
        ))}
        <div>
          <Label>Reminders</Label>
          <div className="mt-1 divide-y divide-border">
            {reminderTouches.map((touch) => (
              <div key={touch.id ?? touch.offset} className="py-1 text-body-sm text-foreground">
                {reminderOffsetLabel(touch.offset)} ·{' '}
                <span className="text-muted-foreground">
                  {touch.offset > 0 ? 'Final warning' : 'Reminder'}
                </span>
              </div>
            ))}
            {reminderTouches.length === 0 && (
              <p className="py-1 text-body-sm text-muted-foreground">No reminders</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={!Number.isInteger(Number(graceDays))} onClick={onSave}>
            Save level settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function reminderOffsetLabel(offset: number) {
  if (offset === 0) return 'On due date'
  return `${Math.abs(offset)} days ${offset < 0 ? 'before' : 'after'} due`
}

type ReminderTouch = { id?: string; offset: number }

function LevelRow({
  level,
  draft,
  onEdit,
  onUseDefaults,
  associationGraceDays,
  associationTouches,
}: {
  level: MembershipLevel
  draft?: LevelRenewalSettingsDraft
  onEdit: (level: MembershipLevel) => void
  onUseDefaults: (level: MembershipLevel) => void
  associationGraceDays: number
  associationTouches: Array<{ id?: string; offset: number }>
}) {
  const settings = level.renewal_settings
  const custom = draft ? draft.renewal_grace_days_override !== null : settings?.inherited === false
  const graceDays = draft
    ? (draft.renewal_grace_days_override ?? associationGraceDays)
    : (settings?.effective_grace_days ?? 0)
  const reminders = draft
    ? (draft.renewal_reminder_touches_override ?? associationTouches)
    : (settings?.effective_reminder_touches ?? [])
  return (
    <div className="flex min-h-10 items-center gap-3 py-2">
      <span className="w-44 shrink-0 text-body-sm text-foreground">{level.name}</span>
      {custom ? (
        <>
          <Badge variant="neutral">Custom</Badge>
          <span className="text-body-sm text-muted-foreground">
            grace {graceDays} days · {reminders.length} reminders
          </span>
          <div className="ml-auto flex gap-0.5">
            <ExpandingIconButton
              icon={Pencil}
              label={`Edit ${level.name}`}
              onClick={() => onEdit(level)}
            />
            <ExpandingIconButton
              icon={Undo2}
              label={`Use defaults ${level.name}`}
              onClick={() => onUseDefaults(level)}
            />
          </div>
        </>
      ) : (
        <>
          <span className="text-body-sm text-muted-foreground">
            grace {graceDays} days · {reminders.length} reminders
          </span>
          <div className="ml-auto">
            <ExpandingIconButton
              icon={SlidersHorizontal}
              label={`Customize ${level.name}`}
              onClick={() => onEdit(level)}
            />
          </div>
        </>
      )}
    </div>
  )
}

interface Props {
  levels: MembershipLevel[]
  drafts: Record<number, LevelRenewalSettingsDraft>
  errors: string[]
  associationGraceDays: number
  associationTouches: Array<{ id?: string; offset: number }>
  onDraftChange: (level: MembershipLevel, draft: LevelRenewalSettingsDraft) => void
}
